import type { Line } from 'd3-shape';
import { line } from 'd3-shape';
import type { Observable, OperatorFunction, UnaryFunction } from 'rxjs';
import {
    asyncScheduler,
    combineLatest,
    combineLatestWith,
    distinctUntilChanged,
    identity,
    map,
    observeOn,
    of,
    pipe,
    switchMap,
} from 'rxjs';
import { distanceConverter } from '../../utils/formatting';
// Dirty hacking of the XMLHttpRequest prototype.
// Things using this should be converted to use fetch instead.
import '../../utils/request';
import { fitVkarToFreqLineIntoRange } from '../spectrum';
import { createLogarithmicScaleDomain } from '../axis';
import { BaseGraphPlugin } from './base-provider';
import { limitLine } from '../graph-helpers';
import { ScaleType } from '../enums';
import type { LineData, LinePoint } from '../dataset';

class BaseAlarmLinesProvider extends BaseGraphPlugin {
    extraShouldRenderCheck(): UnaryFunction<Observable<boolean>, Observable<boolean>> {
        return identity;
    }

    extraLineModifications(): UnaryFunction<Observable<LineData[]>, Observable<LineData[]>> {
        return identity;
    }

    drawFlatLine(): boolean {
        return false;
    }

    init(): void {
        const lines$ = this.graph.data.dataTypeSettings$.pipe(
            map(({ showGuideLineLines }) => showGuideLineLines),
            this.extraShouldRenderCheck(),
            distinctUntilChanged(),
            switchMap((shouldRender: boolean) =>
                shouldRender ? this.graph.data.categoryAndAlarmLines$ : of([])
            ),
            this.extraLineModifications()
        );

        const drawLine$ = combineLatest([
            this.graph.xAxis.scaleUpdated$,
            this.graph.yAxis.scaleUpdated$,
        ]).pipe(
            // Since the scale can be updated by changing scaleType$ and the outcome of
            // lines$ also depends on the last scaleType$ value, we now have to wait until
            // all subscribers on scaleType$ got updated.
            observeOn(asyncScheduler),
            map(
                (): Line<LinePoint> =>
                    line<LinePoint>()
                        .x((d) =>
                            this.drawFlatLine()
                                ? // For the time graph a flat line spanning the whole graph is
                                  // sufficient.
                                  this.graph.xAxis.scale(
                                      d[1] === 0
                                          ? this.graph.xAxis.getMin()
                                          : this.graph.xAxis.getMax()
                                  )
                                : this.graph.xAxis.scale(d[1])
                        )
                        .y((d) =>
                            this.graph.yAxis.scale(
                                distanceConverter(d[0], this.graph.profile.distanceUnit)
                            )
                        )
            )
        );

        const renderer = combineLatest([lines$, drawLine$]).subscribe(([lines, drawLine]) => {
            // Render lines.
            this.graph.drawArea
                .selectAll('.catLine, .alarmLine')
                .data(lines)
                .join('svg:path')
                .attr('class', (d) =>
                    d.type === 'alarm' ? `alarmLine alarmLine${d.alarmIndex}` : 'catLine usedLine'
                )
                .attr('d', (d) => drawLine(d.line));
        });

        this.subscription.add(renderer);
    }
}

export class ValueTimeGraphAlarmLinesProvider extends BaseAlarmLinesProvider {
    drawFlatLine(): boolean {
        return true;
    }

    extraShouldRenderCheck(): OperatorFunction<boolean, boolean> {
        return pipe(
            switchMap((shouldRender) =>
                // The time chart can only draw flat lines. So if the data type indicates
                // that the alarm lines could be drawn, we also need to know whether the
                // alarm line is even a flat line at all.
                shouldRender ? this.graph.data.categoryLineIsFlat$ : of(false)
            )
        );
    }
}

export class ValueFrequencyGraphAlarmLinesProvider extends BaseAlarmLinesProvider {
    extraLineModifications(): OperatorFunction<LineData[], LineData[]> {
        return pipe(
            combineLatestWith(this.graph.xAxis.scaleType$),
            combineLatestWith(this.graph.data.dataTypeSettings$),
            map(([[lines, scaleType], dataTypeSettings]) => {
                const makeLineFitScale =
                    scaleType === ScaleType.Logarithmic
                        ? // For the logarithmic scale type we must modify
                          // the line to start from 1 and end at 250.
                          // If the line starts at 0 we get `Nan` errors when interpolating
                          // the screen locations. If the line does not end at 100, its just ugly.
                          (l: LinePoint[]) =>
                              fitVkarToFreqLineIntoRange(
                                  l,
                                  createLogarithmicScaleDomain(this.graph.profile.distanceUnit)
                                      .x as [number, number]
                              )
                        : // Do nothing when scale type is linear.
                          identity;

                return lines.map((l) => {
                    const limit = dataTypeSettings.valueScaleMax;
                    return {
                        ...l,
                        line: limitLine(makeLineFitScale(l.line), limit * 1.2) as LinePoint[],
                    };
                });
            })
        );
    }
}
